import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Pagination,
  MenuItem,
  Select,
  Skeleton,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandLess as ExpandLessIcon,
  Inventory as InventoryIcon,
  PropaneTank as CylinderIcon,
  FileDownload as ExportIcon,
  FileUpload as ImportIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useColorMode } from "../../contexts/ColorModeContext";
import { getErrorMessage } from "../../utils/errorHandler";
import ConfirmModal from "../../components/common/ConfirmModal";
import { getAllGoods, saveGoods, updateGoods, deleteGoods, exportGoods, importGoods } from "../../api/goodsApi";
import { createCylinder } from "../../api/cylinderMasterApi";
import "./Goods.css";

const Goods = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useColorMode();

  const cm = {
    page: isDark ? "#0B1220" : "#F6F8FB",
    card: isDark ? "#1E293B" : "#FFFFFF",
    surface: isDark ? "#172236" : "#F8FAFC",
    tableHead: isDark ? "#172236" : "#F8FAFC",
    row: isDark ? "#1E293B" : "#FFFFFF",
    rowHover: isDark ? "#26364F" : "#F8FAFC",
    border: isDark ? "#334155" : "#E2E8F0",
    textPrimary: isDark ? "#F8FAFC" : "#0F172A",
    textSecondary: isDark ? "#CBD5E1" : "#64748B",
    muted: isDark ? "#94A3B8" : "#94A3B8",
  };

  const AddCylinderIcon = () => (
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      <CylinderIcon sx={{ fontSize: 20 }} />
      <Box sx={{
        position: 'absolute',
        top: -4,
        right: -4,
        backgroundColor: '#2563EB',
        color: 'white',
        borderRadius: '50%',
        width: 16,
        height: 16,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 'bold',
        lineHeight: 1,
        border: '2px solid white',
        zIndex: 1,
      }}>
        +
      </Box>
    </Box>
  );

  const [goodsName, setGoodsName] = useState("");
  const [goodsList, setGoodsList] = useState([]);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, goodsId: null });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Add/Edit Good Modal States
  const [isGoodModalOpen, setIsGoodModalOpen] = useState(false);
  const [isSavingGood, setIsSavingGood] = useState(false);

  // Add Cylinder Modal States
  const [isAddCylinderModalOpen, setIsAddCylinderModalOpen] = useState(false);
  const [isSavingCylinder, setIsSavingCylinder] = useState(false);
  const [cylinderFormData, setCylinderFormData] = useState({
    cylinderName: "",
    batchNo: "",
    srno: "",
    manufacturingDate: "",
    testingDate: "",
    cylinderCapacity: "",
  });

  // Import/Export States
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const filterInputSx = {
    width: "100%",
    "& .MuiInputLabel-root": {
      fontSize: "14px",
      fontWeight: 500,
      color: cm.textSecondary,
      marginBottom: "8px",
      "&.Mui-focused": {
        color: "#4F46E5",
      },
    },
    "& .MuiInputBase-root": {
      height: 46,
      borderRadius: "12px",
      backgroundColor: cm.surface,
      transition: "all .25s ease",
      marginTop: "4px",
      "&:before": {
        borderBottom: "1px solid #E2E8F0",
      },
      "&:hover:not(.Mui-disabled):before": {
        borderBottom: "1px solid #CBD5E1",
      },
      "&:hover": {
        backgroundColor: cm.card,
      },
      "&.Mui-focused": {
        backgroundColor: cm.card,
        "&:before": {
          borderBottom: "2px solid #4F46E5",
        },
        "&:after": {
          borderBottom: "2px solid #4F46E5",
        },
      }
    },
    "& .MuiInputBase-input": {
      height: "46px",
      boxSizing: "border-box",
      padding: "16.5px 14px",
      display: "flex",
      alignItems: "center",
      fontSize: "14px",
      color: cm.textPrimary,
    },
  };

  useEffect(() => {
    fetchGoods();
  }, []);

  useEffect(() => {
    const quickSearch = location.state?.quickSearch;
    if (quickSearch) {
      setSearchTerm(String(quickSearch));
    }
  }, [location.state]);

  // ---------- Add/Edit Good modal handlers ----------
  const handleOpenAddGoodModal = () => {
    setGoodsName("");
    setEditId(null);
    setIsGoodModalOpen(true);
  };

  const handleOpenEditGoodModal = (item) => {
    setGoodsName(item.name);
    setEditId(item.id);
    setIsGoodModalOpen(true);
  };

  const handleCloseGoodModal = () => {
    setIsGoodModalOpen(false);
    setGoodsName("");
    setEditId(null);
  };

  const handleSaveGood = async () => {
    const name = goodsName.trim();

    if (!name) {
      toast.warning("Goods name is required");
      return;
    }

    setIsSavingGood(true);
    try {
      if (editId) {
        await updateGoods(editId, { name });
        toast.success("Goods updated successfully");
      } else {
        await saveGoods({ name });
        toast.success("Goods saved successfully");
      }

      handleCloseGoodModal();
      fetchGoods();
    } catch (error) {
      handleError(error);
    } finally {
      setIsSavingGood(false);
    }
  };

  // ---------- Add Cylinder modal handlers ----------
  const handleOpenAddCylinderModal = (item) => {
    setCylinderFormData({
      cylinderName: item.name || "",
      batchNo: "",
      srno: "",
      manufacturingDate: "",
      testingDate: "",
      cylinderCapacity: "",
    });
    setIsAddCylinderModalOpen(true);
  };

  const handleCylinderFormChange = (field, value) => {
    setCylinderFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveNewCylinder = async () => {
    if (!cylinderFormData.cylinderName.trim()) {
      toast.error("Cylinder name is required");
      return;
    }
    if (!cylinderFormData.srno.trim()) {
      toast.error("SR No is required");
      return;
    }

    setIsSavingCylinder(true);
    try {
      await createCylinder(cylinderFormData);
      toast.success("Cylinder added successfully");
      setIsAddCylinderModalOpen(false);
      setCylinderFormData({
        cylinderName: "",
        batchNo: "",
        srno: "",
        manufacturingDate: "",
        testingDate: "",
        cylinderCapacity: "",
      });
    } catch (error) {
      console.error("Error creating cylinder:", error);
      toast.error(error.response?.data?.message || "Failed to add cylinder");
    } finally {
      setIsSavingCylinder(false);
    }
  };

  const handleCloseAddCylinderModal = () => {
    setIsAddCylinderModalOpen(false);
    setCylinderFormData({
      cylinderName: "",
      batchNo: "",
      srno: "",
      manufacturingDate: "",
      testingDate: "",
      cylinderCapacity: "",
    });
  };

  // Export/Import Handlers
  const handleExport = async () => {
    try {
      console.log("Starting export...");
      const response = await exportGoods();
      console.log("Full response:", response);
      console.log("Response data:", response.data);
      console.log("Response data type:", typeof response.data);
      console.log("Response data constructor:", response.data?.constructor?.name);
      console.log("Response headers:", response.headers);
      
      const blob = response.data;
      console.log("Export response received, blob size:", blob?.size, "type:", blob?.type);
      
      if (!blob || blob.size === 0) {
        throw new Error("Empty response from server");
      }
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `products-export-${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Products exported successfully");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export products: " + error.message);
    }
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImportFile(file);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.warning("Please select a file first");
      return;
    }

    setIsImporting(true);
    try {
      const res = await importGoods(importFile);
      setImportResult(res.data);
      
      if (res.data.failedCount > 0) {
        toast.warning(`Imported ${res.data.successCount} products, ${res.data.failedCount} failed`);
        if (res.data.errorReport) {
          const byteCharacters = atob(res.data.errorReport);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const errorBlob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          const errorUrl = window.URL.createObjectURL(errorBlob);
          const errorLink = document.createElement("a");
          errorLink.href = errorUrl;
          errorLink.download = `import-errors-${new Date().toISOString().split("T")[0]}.xlsx`;
          document.body.appendChild(errorLink);
          errorLink.click();
          document.body.removeChild(errorLink);
          window.URL.revokeObjectURL(errorUrl);
        }
      } else {
        toast.success(`Successfully imported ${res.data.successCount} products`);
      }
      
      setImportFile(null);
      setIsImportModalOpen(false);
      fetchGoods();
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.message || "Failed to import products");
    } finally {
      setIsImporting(false);
    }
  };

  const handleCloseImportModal = () => {
    setIsImportModalOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  const fetchGoods = async () => {
    try {
      setLoading(true);
      const res = await getAllGoods();
      const goodsData = res.data?.content || res.data || [];
      setGoodsList(Array.isArray(goodsData) ? goodsData : []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load goods");
      setGoodsList([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirmModal({ isOpen: true, goodsId: id });
  };

  const confirmDelete = async () => {
    try {
      await deleteGoods(confirmModal.goodsId);
      toast.success("Goods deleted successfully");
      fetchGoods();
      setConfirmModal({ isOpen: false, goodsId: null });
    } catch (error) {
      handleError(error);
      setConfirmModal({ isOpen: false, goodsId: null });
    }
  };

  const cancelDelete = () => {
    setConfirmModal({ isOpen: false, goodsId: null });
  };

  const handleError = (error) => {
    const status = error?.response?.status;

    if (status === 401) {
      toast.error("Unauthorized - Please login again");
      return;
    }

    if (status === 403) {
      toast.error("Access denied");
      return;
    }

    // The API returns the actual duplicate message with HTTP 409.
    // Always prefer that message so the user sees the backend reason.
    if (status === 409) {
      toast.error(getErrorMessage(error));
      return;
    }

    if (status === 400) {
      toast.warning(getErrorMessage(error));
      return;
    }

    if (status === 404) {
      toast.warning(getErrorMessage(error));
      return;
    }

    toast.error(getErrorMessage(error));
  };

  const handleAddCylinder = () => {
    navigate("/dashboard/inventory/cylinders");
  };

  const clearFilters = () => {
    setSearchTerm("");
    setPage(0);
  };

  const handlePageChange = (event, value) => {
    setPage(value - 1);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredGoods = goodsList.filter(
    (item) =>
      item.name &&
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedGoods = filteredGoods.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalPages = Math.ceil(filteredGoods.length / rowsPerPage);

  return (
    <div className="goodsContainer">
      <div className="goodsContent">
        <Box sx={{ p: "8px 4px 36px" }}>
          <ConfirmModal
            isOpen={confirmModal.isOpen}
            onConfirm={confirmDelete}
            onCancel={cancelDelete}
            title="Delete Item"
            message="Are you sure you want to delete this item? This action cannot be undone."
            confirmText="Delete Item"
            cancelText="Cancel"
          />

      {/* Page Header */}
      <Box className="goods-header" sx={{ mb: { xs: 3, md: 4 }, display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 } }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: { xs: 56, md: 64 },
          height: { xs: 56, md: 64 },
          background: 'linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 100%)',
          borderRadius: { xs: '12px', md: '16px' },
          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.15)',
        }}>
          <InventoryIcon sx={{ fontSize: { xs: 32, md: 40 }, color: '#4F46E5' }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 600, color: cm.textPrimary, mb: 0.5 }}>
            Goods Management
          </Typography>
          <Typography variant="body1" sx={{ color: cm.textSecondary, fontSize: { xs: '0.8125rem', md: '0.875rem' } }}>
            Manage your inventory goods and add cylinders
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddGoodModal}
          sx={{
            height: 44,
            borderRadius: "10px",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "14px",
            backgroundColor: "#16A34A",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#15803D",
            },
          }}
        >
          Add Good
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddCylinder}
          sx={{
            height: 44,
            borderRadius: "10px",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "14px",
            backgroundColor: "#4F46E5",
            color: "#fff",
            "&:hover": {
              backgroundColor: "#4338CA",
            },
          }}
        >
          Add Cylinder
        </Button>
        <Button
          variant="outlined"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          sx={{
            height: 44,
            borderRadius: "10px",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "14px",
            borderColor: "#E5E7EB",
            color: "#64748b",
            "&:hover": {
              borderColor: "#2563eb",
              background: "#eff6ff",
              color: "#2563eb",
            },
          }}
        >
          Export
        </Button>
        <Button
          variant="outlined"
          startIcon={<ImportIcon />}
          onClick={() => setIsImportModalOpen(true)}
          sx={{
            height: 44,
            borderRadius: "10px",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "14px",
            borderColor: "#E5E7EB",
            color: "#64748b",
            "&:hover": {
              borderColor: "#059669",
              background: "#ECFDF5",
              color: "#059669",
            },
          }}
        >
          Import
        </Button>
      </Box>

      {/* Filters (search only) */}
      {!filtersOpen ? (
        <Button
          variant="outlined"
          startIcon={<SearchIcon />}
          onClick={() => setFiltersOpen(true)}
          sx={{
            mb: 3,
            height: 48,
            borderRadius: "10px",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "14px",
            borderColor: "#E5E7EB",
            color: "#64748b",
            "&:hover": {
              borderColor: "#2563eb",
              background: "#eff6ff",
              color: "#2563eb",
            },
          }}
        >
          Show Filters
        </Button>
      ) : (
        <Card className="goods-filters-card"
          sx={{
            mb: 3,
            borderRadius: "22px",
            border: "1px solid #E5E7EB",
            boxShadow: "0 10px 30px rgba(15,23,42,.05)",
            overflow: "visible",
            background: cm.card
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              "&:last-child": {
                pb: { xs: 2, sm: 2.5, md: 3 }
              }
            }}
          >
            {/* Header row */}
            <Box sx={{
              display: "flex",
              alignItems: { xs: "center", sm: "flex-start" },
              justifyContent: "space-between",
              mb: { xs: 2, sm: 2.5 },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 }
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }} onClick={() => setFiltersOpen(false)}>
                <Box sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#eff6ff",
                  color: "#2563eb",
                }}>
                  <SearchIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
                  Filters
                </Typography>
                <ExpandLessIcon sx={{ fontSize: 20, color: "#64748b" }} />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                  onClick={clearFilters}
                  sx={{
                    color: "#64748b",
                    fontWeight: 500,
                    fontSize: "13px",
                    textTransform: "none",
                    "&:hover": {
                      background: "#f1f5f9",
                      color: "#0f172a"
                    }
                  }}
                >
                  Clear All
                </Button>
              </Box>
            </Box>

            {/* Filter inputs - search only */}
            <Grid container spacing={2.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Search Goods"
                  placeholder="Search goods by name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon sx={{ mr: 1, color: '#94A3B8' }} />,
                  }}
                  sx={filterInputSx}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Box className="goods-table-card" sx={{
        backgroundColor: cm.card,
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px',
        boxSizing: 'border-box',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        {loading ? (
          <Box>
            <TableContainer
              sx={{
                minHeight: 500,
                overflowX: 'auto',
                maxWidth: '100%',
              }}
            >
              <Table sx={{
                minWidth: { xs: 1200, md: '100%' },
                width: '100%',
                tableLayout: { md: 'auto', xs: 'fixed' }
              }}>
                <TableHead sx={{
                  '& th': { position: 'sticky', top: 0, zIndex: 5, background: cm.card }
                }}>
                  <TableRow>
                    <TableCell 
                      align="left"
                      sx={{ 
                        fontWeight: 700, 
                        backgroundColor: cm.surface, 
                        fontSize: 13, 
                        padding: { xs: '12px 10px', md: '16px' },
                      }}
                    >
                      Product Name
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 700, 
                        backgroundColor: cm.surface, 
                        fontSize: 13, 
                        padding: { xs: '12px 10px', md: '16px' },
                        width: { xs: 160, md: 180 },
                        minWidth: { xs: 160, md: 180 },
                        textAlign: 'center',
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton variant="text" width="60%" height={24} />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: '8px' }} />
                          <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: '8px' }} />
                          <Skeleton variant="rectangular" width={36} height={36} sx={{ borderRadius: '8px' }} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ) : (
          <>
            <TableContainer
              sx={{
                minHeight: 500,
                overflowX: 'auto',
                maxWidth: '100%',
                "@media (min-width: 1024px)": {
                  overflowX: 'hidden',
                },
                "@media (max-width: 1023px)": {
                  overflowX: 'auto',
                },
                "&::-webkit-scrollbar": {
                  height: 8,
                },
                "&::-webkit-scrollbar-thumb": {
                  borderRadius: 20,
                  background: "#cbd5e1",
                },
              }}
            >
              <Table sx={{
                minWidth: { xs: 1200, md: '100%' },
                width: '100%',
                tableLayout: { md: 'auto', xs: 'fixed' }
              }}>
                <TableHead sx={{
                  '& th': { position: 'sticky', top: 0, zIndex: 5, background: cm.card }
                }}>
                  <TableRow>
                    <TableCell 
                      align="left"
                      sx={{ 
                        fontWeight: 700, 
                        backgroundColor: cm.surface, 
                        fontSize: 13, 
                        padding: { xs: '12px 10px', md: '16px' },
                      }}
                    >
                      Product Name
                    </TableCell>
                    <TableCell 
                      align="center" 
                      sx={{ 
                        fontWeight: 700, 
                        backgroundColor: cm.surface, 
                        fontSize: 13, 
                        padding: { xs: '12px 10px', md: '16px' },
                        width: { xs: 160, md: 180 },
                        minWidth: { xs: 160, md: 180 },
                        textAlign: 'center',
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!filteredGoods || filteredGoods.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={2} align="center" sx={{ py: 8 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ fontSize: 48, opacity: 0.5 }}>📦</Box>
                          <Typography variant="h6" sx={{ color: cm.textPrimary, fontWeight: 600 }}>
                            No Goods Found
                          </Typography>
                          <Typography variant="body2" sx={{ color: cm.textSecondary }}>
                            Try adjusting your search or add new goods
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedGoods.map((item) => (
                      <TableRow
                        key={item.id}
                        hover
                        sx={{ 
                          '&:hover': { backgroundColor: cm.surface },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                          {item.name}
                        </TableCell>
                        <TableCell 
                          align="center" 
                          sx={{ 
                            fontSize: 13, 
                            padding: { xs: '12px 10px', md: '16px' },
                            width: { xs: 160, md: 180 },
                            minWidth: { xs: 160, md: 180 },
                            textAlign: 'center',
                          }}
                        >
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1, 
                            justifyContent: 'center', 
                            alignItems: 'center' 
                          }}>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditGoodModal(item)}
                              sx={{ 
                                color: '#F59E0B',
                                width: 36, 
                                height: 36, 
                                flexShrink: 0,
                                borderRadius: '8px',
                                '&:hover': { 
                                  background: '#FEF3C7',
                                  transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenAddCylinderModal(item)}
                              sx={{ 
                                color: '#2563EB',
                                width: 36, 
                                height: 36, 
                                flexShrink: 0,
                                borderRadius: '8px',
                                '&:hover': { 
                                  background: '#DBEAFE',
                                  transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <AddCylinderIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(item.id)}
                              sx={{ 
                                color: '#EF4444',
                                width: 36, 
                                height: 36, 
                                flexShrink: 0,
                                borderRadius: '8px',
                                '&:hover': { 
                                  background: '#FEE2E2',
                                  transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination */}
            {!loading && filteredGoods.length > 0 && (
              <Box className="goods-pagination"
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  p: 2,
                  borderTop: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: { xs: "center", md: "flex-start" } }}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                    Showing {page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, filteredGoods.length)} of {filteredGoods.length} records
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                  <Pagination
                    count={totalPages}
                    page={page + 1}
                    onChange={handlePageChange}
                    disabled={loading}
                    shape="rounded"
                    variant="outlined"
                    color="primary"
                    showFirstButton
                    showLastButton
                    siblingCount={0}
                    boundaryCount={1}
                    size="small"
                    sx={{
                      "& .MuiPaginationItem-root": {
                        transition: "transform .2s, box-shadow .2s, background-color .2s",
                        borderRadius: "10px",
                        fontWeight: 600,
                        minWidth: 38,
                        height: 38,
                      },
                      "& .MuiPaginationItem-root:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 10px rgba(0,0,0,.15)",
                      },
                      "& .MuiPaginationItem-root:active": {
                        transform: "scale(.95)",
                      },
                    }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.875rem" }}>
                      Rows per page:
                    </Typography>
                    <Select
                      value={rowsPerPage}
                      onChange={handleRowsPerPageChange}
                      size="small"
                      sx={{
                        minWidth: 70,
                        height: 32,
                        borderRadius: "8px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E2E8F0",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#CBD5E1",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#2563EB",
                        },
                      }}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                    </Select>
                  </Box>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* Add/Edit Good Modal */}
      <Dialog
        open={isGoodModalOpen}
        onClose={handleCloseGoodModal}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "20px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              overflow: "hidden",
            }
          }
        }}
      >
        <Box sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
        }}>
          <Box sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: "12px",
            backgroundColor: "#DCFCE7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
            <InventoryIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: "#16A34A" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DialogTitle sx={{ 
              fontSize: { xs: "1.25rem", sm: "1.5rem" }, 
              fontWeight: 700, 
              color: cm.textPrimary,
              p: 0,
              mb: 0.5,
              lineHeight: 1.2,
            }}>
              {editId ? "Edit Good" : "Add Good"}
            </DialogTitle>
            <Typography sx={{ 
              fontSize: { xs: "0.8rem", sm: "0.875rem" }, 
              color: cm.textSecondary,
              fontWeight: 400,
            }}>
              {editId ? "Update the good's details below" : "Enter the details for the new good"}
            </Typography>
          </Box>
        </Box>
        <DialogContent sx={{ 
          pt: { xs: 2, sm: 3 }, 
          pb: { xs: 1.5, sm: 2 }, 
          px: { xs: 2, sm: 3 },
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography sx={{ 
              fontSize: { xs: "13px", sm: "14px" }, 
              fontWeight: 500, 
              color: cm.textSecondary,
              ml: 1,
            }}>
              Goods Name
            </Typography>
            <TextField
              fullWidth
              autoFocus
              value={goodsName}
              onChange={(e) => setGoodsName(e.target.value)}
              placeholder="Enter goods name"
              variant="outlined"
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveGood();
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: { xs: 42, sm: 48 },
                  borderRadius: "12px",
                  background: "#F8FAFC",
                  "& fieldset": {
                    borderColor: "#E2E8F0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#CBD5E1",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#16A34A",
                    borderWidth: 2,
                  },
                },
                "& .MuiInputBase-input": {
                  height: { xs: 42, sm: 48 },
                  padding: { xs: "10px 14px", sm: "12px 14px" },
                  fontSize: { xs: "13px", sm: "14px" },
                  boxSizing: "border-box",
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 }, 
          gap: 2,
        }}>
          <Button 
            onClick={handleCloseGoodModal}
            disabled={isSavingGood}
            sx={{
              height: { xs: 44, sm: 48 },
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "14px", sm: "15px" },
              px: { xs: 2, sm: 3 },
              borderColor: "#E2E8F0",
              color: cm.textSecondary,
              backgroundColor: "white",
              "&:hover": {
                borderColor: "#CBD5E1",
                backgroundColor: "#F1F5F9",
                color: cm.textPrimary,
              },
              "&:disabled": {
                opacity: 0.6,
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveGood} 
            variant="contained"
            disabled={isSavingGood}
            sx={{
              height: { xs: 44, sm: 48 },
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "14px", sm: "15px" },
              px: { xs: 2, sm: 3 },
              background: "#16A34A",
              "&:hover": {
                background: "#15803D",
                boxShadow: "0 4px 12px rgba(22, 163, 74, 0.4)",
              },
              "&:disabled": {
                background: "#94A3B8",
                boxShadow: "none",
              }
            }}
          >
            {isSavingGood ? "Saving..." : (editId ? "Update Good" : "Save Good")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Cylinder Modal */}
      <Dialog
        open={isAddCylinderModalOpen}
        onClose={handleCloseAddCylinderModal}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: "20px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              overflow: "hidden",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
            }
          }
        }}
      >
        <Box sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
        }}>
          <Box sx={{
            width: { xs: 40, sm: 48 },
            height: { xs: 40, sm: 48 },
            borderRadius: "12px",
            backgroundColor: "#EEF2FF",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}>
            <CylinderIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: "#4F46E5" }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <DialogTitle sx={{ 
              fontSize: { xs: "1.25rem", sm: "1.5rem" }, 
              fontWeight: 700, 
              color: cm.textPrimary,
              p: 0,
              mb: 0.5,
              lineHeight: 1.2,
            }}>
              Add Cylinder Master
            </DialogTitle>
            <Typography sx={{ 
              fontSize: { xs: "0.8rem", sm: "0.875rem" }, 
              color: cm.textSecondary,
              fontWeight: 400,
            }}>
              Fill in the cylinder details below
            </Typography>
          </Box>
        </Box>
        <DialogContent sx={{ 
          pt: { xs: 2, sm: 3 }, 
          pb: { xs: 1.5, sm: 2 }, 
          px: { xs: 2, sm: 3 },
          overflowY: "auto",
          flex: 1,
        }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} sm={6} md={4} sx={{ width: "100%" }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: cm.textSecondary,
                  ml: 1,
                }}>
                  Cylinder Name
                </Typography>
                <TextField
                  fullWidth
                  value={cylinderFormData.cylinderName}
                  onChange={(e) => handleCylinderFormChange("cylinderName", e.target.value)}
                  disabled
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F1F5F9",
                      color: cm.textSecondary,
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ width: "100%" }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: cm.textSecondary,
                  ml: 1,
                }}>
                  Batch No
                </Typography>
                <TextField
                  fullWidth
                  value={cylinderFormData.batchNo}
                  onChange={(e) => handleCylinderFormChange("batchNo", e.target.value)}
                  placeholder="Enter batch number"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ width: "100%" }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: cm.textSecondary,
                  ml: 1,
                }}>
                  SR No
                </Typography>
                <TextField
                  fullWidth
                  value={cylinderFormData.srno}
                  onChange={(e) => handleCylinderFormChange("srno", e.target.value)}
                  placeholder="Enter serial number"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ width: "100%" }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: cm.textSecondary,
                  ml: 1,
                }}>
                  Capacity
                </Typography>
                <TextField
                  fullWidth
                  value={cylinderFormData.cylinderCapacity}
                  onChange={(e) => handleCylinderFormChange("cylinderCapacity", e.target.value)}
                  placeholder="Enter cylinder capacity"
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ width: "100%" }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: cm.textSecondary,
                  ml: 1,
                }}>
                  Manufacturing Date
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={cylinderFormData.manufacturingDate}
                  onChange={(e) => handleCylinderFormChange("manufacturingDate", e.target.value)}
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={4} sx={{ width: "100%" }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
                <Typography sx={{ 
                  fontSize: { xs: "13px", sm: "14px" }, 
                  fontWeight: 500, 
                  color: cm.textSecondary,
                  ml: 1,
                }}>
                  Testing Date
                </Typography>
                <TextField
                  fullWidth
                  type="date"
                  value={cylinderFormData.testingDate}
                  onChange={(e) => handleCylinderFormChange("testingDate", e.target.value)}
                  variant="outlined"
                  sx={{
                    width: "100%",
                    "& .MuiOutlinedInput-root": {
                      width: "100%",
                      height: { xs: 42, sm: 48 },
                      borderRadius: "12px",
                      background: "#F8FAFC",
                      "& fieldset": {
                        borderColor: "#E2E8F0",
                      },
                      "&:hover fieldset": {
                        borderColor: "#CBD5E1",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "#4F46E5",
                        borderWidth: 2,
                      },
                    },
                    "& .MuiInputBase-input": {
                      height: { xs: 42, sm: 48 },
                      padding: { xs: "10px 14px", sm: "12px 14px" },
                      fontSize: { xs: "13px", sm: "14px" },
                      boxSizing: "border-box",
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 }, 
          gap: 2,
          flexShrink: 0,
        }}>
          <Button 
            onClick={handleCloseAddCylinderModal}
            variant="outlined"
            disabled={isSavingCylinder}
            sx={{
              height: { xs: 44, sm: 48 },
              minWidth: { xs: 96, sm: 112 },
              flexShrink: 0,
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "14px", sm: "15px" },
              px: { xs: 2, sm: 3 },
              border: "1px solid",
              borderColor: isDark ? "#475569" : "#CBD5E1",
              color: isDark ? "#F8FAFC" : "#475569",
              backgroundColor: isDark ? "#172236" : "#FFFFFF",
              "&:hover": {
                borderColor: isDark ? "#64748B" : "#94A3B8",
                backgroundColor: isDark ? "#273449" : "#F1F5F9",
                color: isDark ? "#FFFFFF" : "#0F172A",
              },
              "&:disabled": {
                opacity: 0.6,
                color: isDark ? "#CBD5E1" : "#64748B",
                borderColor: isDark ? "#475569" : "#E2E8F0",
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveNewCylinder} 
            variant="contained"
            disabled={isSavingCylinder}
            sx={{
              height: { xs: 44, sm: 48 },
              borderRadius: "12px",
              fontWeight: 600,
              textTransform: "none",
              fontSize: { xs: "14px", sm: "15px" },
              px: { xs: 2, sm: 3 },
              background: "#4F46E5",
              "&:hover": {
                background: "#4338CA",
                boxShadow: "0 4px 12px rgba(79, 70, 229, 0.4)",
              },
              "&:disabled": {
                background: "#94A3B8",
                boxShadow: "none",
              }
            }}
          >
            {isSavingCylinder ? "Saving..." : "Save Cylinder"}
          </Button>
        </DialogActions>
      </Dialog>
        {/* Import Modal */}
        <Dialog
          open={isImportModalOpen}
          onClose={handleCloseImportModal}
          maxWidth="sm"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: "20px",
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                overflow: "hidden",
              }
            }
          }}
        >
          <Box sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            display: "flex",
            alignItems: "center",
            gap: { xs: 1.5, sm: 2 },
          }}>
            <Box sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: "12px",
              backgroundColor: "#ECFDF5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}>
              <ImportIcon sx={{ fontSize: { xs: 24, sm: 28 }, color: "#059669" }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <DialogTitle sx={{ 
                fontSize: { xs: "1.25rem", sm: "1.5rem" }, 
                fontWeight: 700, 
                color: cm.textPrimary,
                p: 0,
                mb: 0.5,
                lineHeight: 1.2,
              }}>
                Import Products
              </DialogTitle>
              <Typography sx={{ 
                fontSize: { xs: "0.8rem", sm: "0.875rem" }, 
                color: cm.textSecondary,
                fontWeight: 400,
              }}>
                Upload an Excel file with product names
              </Typography>
            </Box>
          </Box>
          <DialogContent sx={{ 
            pt: { xs: 2, sm: 3 }, 
            pb: { xs: 1.5, sm: 2 }, 
            px: { xs: 2, sm: 3 },
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ color: cm.textSecondary }}>
                <strong>Template Format:</strong> The Excel file should have a header row with "Product Name" column.
              </Typography>
              <Typography variant="body2" sx={{ color: cm.textSecondary }}>
                <strong>Example:</strong>
              </Typography>
              <Box sx={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: "8px", p: 2, fontFamily: "monospace", fontSize: "12px", color: cm.textPrimary }}>
                Product Name
                Oxygen
                Nitrogen
                Argon
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', width: "100%", gap: 0.5 }}>
              <Typography sx={{ 
                fontSize: { xs: "13px", sm: "14px" }, 
                fontWeight: 500, 
                color: cm.textSecondary,
                ml: 1,
              }}>
                Select Excel File
              </Typography>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImportFileChange}
                style={{ display: 'none' }}
                id="import-file-input"
              />
              <label htmlFor="import-file-input" sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: { xs: 42, sm: 48 },
                borderRadius: "12px",
                background: "#F8FAFC",
                border: "1px dashed #E2E8F0",
                cursor: "pointer",
                transition: "all .25s ease",
                "&:hover": {
                  background: "#F1F5F9",
                  borderColor: "#CBD5E1",
                }
              }}>
                {importFile ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImportIcon sx={{ color: "#059669" }} />
                    <Typography variant="body2" sx={{ color: "#059669", fontWeight: 500 }}>
                      {importFile.name}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ImportIcon sx={{ color: "#94A3B8" }} />
                    <Typography variant="body2" sx={{ color: "#94A3B8" }}>
                      Click or drag to upload .xlsx file
                    </Typography>
                  </Box>
                )}
              </label>
            </Box>
            {importResult && (
              <Box sx={{ mt: 2, p: 2, background: importResult.failedCount > 0 ? "#FEF2F2" : "#ECFDF5", border: `1px solid ${importResult.failedCount > 0 ? "#FECACA" : "#A7F3D0"}`, borderRadius: "12px" }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: importResult.failedCount > 0 ? "#DC2626" : "#059669" }}>
                    Import Result
                  </Typography>
                  <Typography variant="caption" sx={{ color: cm.textSecondary }}>
                    {importResult.totalRows} rows processed
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  <Box sx={{ px: 2, py: 1, background: "#10B981", color: "white", borderRadius: "8px", fontSize: "12px", fontWeight: 600 }}>
                    ✓ {importResult.successCount} Success
                  </Box>
                  {importResult.failedCount > 0 && (
                    <Box sx={{ px: 2, py: 1, background: "#EF4444", color: "white", borderRadius: "8px", fontSize: "12px", fontWeight: 600 }}>
                      ✗ {importResult.failedCount} Failed
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            p: { xs: 2, sm: 3 }, 
            gap: 2,
            flexShrink: 0,
          }}>
            <Button 
              onClick={handleCloseImportModal}
              disabled={isImporting}
              sx={{
                height: { xs: 44, sm: 48 },
                minWidth: { xs: 96, sm: 112 },
                flexShrink: 0,
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "14px", sm: "15px" },
                px: { xs: 2, sm: 3 },
                border: "1px solid",
                borderColor: isDark ? "#475569" : "#CBD5E1",
                color: isDark ? "#F8FAFC" : "#475569",
                backgroundColor: isDark ? "#172236" : "#FFFFFF",
                "&:hover": {
                  borderColor: isDark ? "#64748B" : "#94A3B8",
                  backgroundColor: isDark ? "#273449" : "#F1F5F9",
                  color: isDark ? "#FFFFFF" : "#0F172A",
                },
                "&:disabled": {
                  opacity: 0.6,
                  color: isDark ? "#CBD5E1" : "#64748B",
                  borderColor: isDark ? "#475569" : "#E2E8F0",
                }
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleImport}
              variant="contained"
              disabled={isImporting || !importFile}
              sx={{
                height: { xs: 44, sm: 48 },
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
                fontSize: { xs: "14px", sm: "15px" },
                px: { xs: 2, sm: 3 },
                background: "#059669",
                "&:hover": {
                  background: "#047857",
                  boxShadow: "0 4px 12px rgba(5, 150, 105, 0.4)",
                },
                "&:disabled": {
                  background: "#94A3B8",
                  boxShadow: "none",
                }
              }}
            >
              {isImporting ? "Importing..." : "Import Products"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  </div>
);
};

export default Goods;